import { forwardRef } from 'react';
import { formatAge, formatDate, titleCase } from '../../utils/format';

/**
 * The printed prescription. Rendered at real paper size so what is on screen
 * is exactly what comes out of the printer.
 */
/**
 * Breaks a long single-line address into at most `max` balanced lines, cutting
 * at commas so it never splits mid-word. A short address is left alone.
 */
function splitAddress(address, max = 2) {
  const text = String(address || '').trim();
  if (!text) return [];

  const parts = text.split(',').map((p) => p.trim()).filter(Boolean);
  if (parts.length <= 1) return [text];

  const lines = [];
  const perLine = Math.ceil(parts.length / max);

  for (let i = 0; i < parts.length; i += perLine) {
    lines.push(parts.slice(i, i + perLine).join(', '));
  }

  return lines.slice(0, max);
}

const PrescriptionSheet = forwardRef(function PrescriptionSheet({ data }, ref) {
  const { clinic, patient, visit, consultation, doctor } = data;
  const v = visit.vitals || {};

  // The signed-in doctor wins; clinic settings are the fallback.
  const doctorName = doctor?.name || clinic.doctorName;
  const doctorQualification = doctor?.qualification || clinic.qualification;
  const doctorSpecialization = doctor?.specialization || '';
  const doctorRegNo = doctor?.registrationNo || clinic.registrationNo;

  const addressLines = splitAddress(clinic.address, 2);

  const vitalsText = [
    v.weight && `Wt ${v.weight} kg`,
    v.bpSystolic && `BP ${v.bpSystolic}/${v.bpDiastolic}`,
    v.pulse && `Pulse ${v.pulse}`,
    v.temperature && `Temp ${v.temperature} F`,
    v.spo2 && `SpO2 ${v.spo2}%`,
  ]
    .filter(Boolean)
    .join('   ');

  return (
    <div className={`rx-sheet ${clinic.paperSize === 'A5' ? 'a5' : ''}`} ref={ref}>
      {clinic.printHeader ? (
        <div className="rx-header">
          <div className="rx-header-clinic">
            <div className="rx-clinic-name">{clinic.clinicName}</div>
            {clinic.tagline && <div className="rx-small">{clinic.tagline}</div>}
            <div className="rx-small" style={{ marginTop: '1.5mm' }}>
              {addressLines.map((line, i) => (
                <div key={i}>{line}</div>
              ))}
              {clinic.phone && (
                <div>
                  Phone: {clinic.phone}
                  {clinic.email ? `  |  ${clinic.email}` : ''}
                </div>
              )}
              {clinic.timings && <div>{clinic.timings}</div>}
            </div>
          </div>

          <div className="rx-header-doctor">
            <div className="rx-doctor-name">{doctorName}</div>
            <div className="rx-small">
              {doctorQualification && <div>{doctorQualification}</div>}
              {doctorSpecialization && <div>{doctorSpecialization}</div>}
              {doctorRegNo && <div>Reg. No: {doctorRegNo}</div>}
            </div>
          </div>
        </div>
      ) : (
        // Pre-printed letterhead: leave the top of the page blank.
        <div style={{ height: `${clinic.headerTopSpaceMm || 35}mm` }} />
      )}

      <div className="rx-patient-strip">
        <div>
          <span className="rx-field-label">Patient ID</span>
          <span className="rx-field-value">{patient.patientId}</span>
        </div>
        <div style={{ gridColumn: 'span 2' }}>
          <span className="rx-field-label">Name</span>
          <span className="rx-field-value">{patient.name}</span>
        </div>
        <div>
          <span className="rx-field-label">Date</span>
          <span className="rx-field-value">{formatDate(visit.visitDate)}</span>
        </div>

        <div>
          <span className="rx-field-label">Age / Sex</span>
          <span className="rx-field-value">
            {formatAge(patient)} / {titleCase(patient.gender).charAt(0)}
          </span>
        </div>
        <div>
          <span className="rx-field-label">Mobile</span>
          <span className="rx-field-value">{patient.mobile}</span>
        </div>
        <div style={{ gridColumn: 'span 2' }}>
          <span className="rx-field-label">Address</span>
          <span className="rx-field-value">
            {[patient.address, patient.city].filter(Boolean).join(', ') || '-'}
          </span>
        </div>

        {vitalsText && (
          <div style={{ gridColumn: 'span 4' }}>
            <span className="rx-field-label">Vitals</span>
            <span className="rx-field-value">{vitalsText}</span>
          </div>
        )}
      </div>

      {patient.allergies?.length > 0 && (
        <div className="rx-allergy">
          ALLERGIC TO: {patient.allergies.join(', ')}
        </div>
      )}

      {consultation.complaints?.length > 0 && (
        <div className="rx-section">
          <div className="rx-section-title">Complaints</div>
          <div>
            {consultation.complaints
              .map((c) => `${c.text}${c.duration ? ` (${c.duration})` : ''}`)
              .join(',  ')}
          </div>
        </div>
      )}

      {consultation.examination && (
        <div className="rx-section">
          <div className="rx-section-title">On Examination</div>
          <div>{consultation.examination}</div>
        </div>
      )}

      {consultation.diagnosis?.length > 0 && (
        <div className="rx-section">
          <div className="rx-section-title">Diagnosis</div>
          <div style={{ fontWeight: 600 }}>{consultation.diagnosis.join(',  ')}</div>
        </div>
      )}

      {consultation.medicines?.length > 0 && (
        <div className="rx-section">
          <div style={{ display: 'flex', alignItems: 'baseline' }}>
            <span className="rx-symbol">Rx</span>
            <div style={{ flex: 1 }}>
              <table className="rx-med-table">
                <thead>
                  <tr>
                    <th style={{ width: '6%' }}>#</th>
                    <th style={{ width: '40%' }}>Medicine</th>
                    <th style={{ width: '18%' }}>Dose</th>
                    <th style={{ width: '22%' }}>When to take</th>
                    <th style={{ width: '14%' }}>Duration</th>
                  </tr>
                </thead>
                <tbody>
                  {consultation.medicines.map((m, i) => (
                    <tr key={i}>
                      <td>{i + 1}</td>
                      <td>
                        <div className="rx-med-name">
                          {m.type} {m.name}
                        </div>
                        {m.instruction && <div className="rx-med-sub">{m.instruction}</div>}
                      </td>
                      <td>
                        {m.dose}
                        <div className="rx-med-sub">{m.frequency}</div>
                      </td>
                      <td>{m.timing}</td>
                      <td>
                        {m.duration} {m.durationUnit}
                        {m.quantity ? <div className="rx-med-sub">Total {m.quantity}</div> : null}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {consultation.testsAdvised?.length > 0 && (
        <div className="rx-section">
          <div className="rx-section-title">Tests Advised</div>
          <ol className="rx-list">
            {consultation.testsAdvised.map((t, i) => (
              <li key={i}>{t}</li>
            ))}
          </ol>
        </div>
      )}

      {consultation.precautions?.length > 0 && (
        <div className="rx-section">
          <div className="rx-section-title">Precautions &amp; Advice</div>
          <ol className="rx-list">
            {consultation.precautions.map((p, i) => (
              <li key={i}>{p}</li>
            ))}
          </ol>
        </div>
      )}

      {consultation.advice && (
        <div className="rx-section">
          <div className="rx-section-title">General Advice</div>
          <div>{consultation.advice}</div>
        </div>
      )}

      {consultation.followUpDate && (
        <div className="rx-section">
          <span className="rx-followup">
            Next visit on {formatDate(consultation.followUpDate)}
          </span>
        </div>
      )}

      <div className="rx-footer">
        <div className="rx-note">
          {clinic.footerNote}
          {clinic.closedOn ? <div>Clinic closed on {clinic.closedOn}.</div> : null}
        </div>
        <div className="rx-sign">
          {doctorName}
          <div style={{ fontSize: '8pt', color: '#555' }}>{doctorQualification}</div>
        </div>
      </div>
    </div>
  );
});

export default PrescriptionSheet;
